<script setup>
import { ref, onMounted, computed } from 'vue';

const props = defineProps({
    exitUrl: { type: String, required: true },
    storeUrl: { type: String, required: true },
    pieceId: { type: String, default: null },
    contentType: { type: String, default: null },
    generateUrl: { type: String, default: null },
    saveUrl: { type: String, default: null },
    conversationUrl: { type: String, default: null },
    pollUrl: { type: String, default: null },
    contextUrl: { type: String, default: null },
    createScheduleUrl: { type: String, default: null },
    historicalPostsUrl: { type: String, required: true },
    historicalPostsStoreUrl: { type: String, required: true },
    flowieHandoff: { type: Object, default: null },
});

const currentContent = ref('');
const chatMessages = ref([]);
const isGenerating = ref(false);
const isImageGenerating = ref(false);
const isSaving = ref(false);
const savingState = ref('');
const pageContext = ref(null);
const pollTimer = ref(null);
const imagePollTimer = ref(null);
const isEditing = ref(false);
const editableContent = ref('');
const showHistoricalPosts = ref(false);
const generateError = ref(null);
const generatingStep = ref('');
const imageUrl = ref(null);
const suggestedSchedule = ref(null);
const scheduleActivated = ref(false);
const activatingSchedule = ref(false);
const copyingState = ref('');

const showOnboarding = computed(() =>
    !props.pieceId && chatMessages.value.length === 0 && !currentContent.value && !pageContext.value && !isGenerating.value
);

const contentType = computed(() => pageContext.value?.content_type || props.contentType || 'other');

const contentTypeLabels = {
    linkedin: 'LinkedIn',
    blog: 'Blog Article',
    instagram: 'Instagram',
    newsletter: 'Newsletter',
    facebook: 'Facebook',
    other: 'Other',
};

const contentTypeColors = {
    linkedin: 'bg-blue-100 text-blue-700',
    blog: 'bg-green-100 text-green-700',
    instagram: 'bg-pink-100 text-pink-700',
    newsletter: 'bg-purple-100 text-purple-700',
    facebook: 'bg-indigo-100 text-indigo-700',
    other: 'bg-gray-100 text-gray-700',
};

onMounted(async () => {
    if (props.pieceId && props.conversationUrl) {
        await loadConversation();
    }
});

const loadConversation = async () => {
    try {
        const res = await fetch(props.conversationUrl);
        const data = await res.json();
        chatMessages.value = data.messages || [];
        currentContent.value = data.content || '';
        imageUrl.value = data.image_url || null;
        if (data.context) {
            pageContext.value = data.context;
            // Restore schedule state from persisted context
            if (data.context.schedule_id) {
                scheduleActivated.value = true;
            }
            if (data.context.suggested_schedule) {
                suggestedSchedule.value = data.context.suggested_schedule;
            }
        }

        // Auto-generate first draft if piece has context but no messages yet
        let firstDraftPrompt = null;
        if (chatMessages.value.length === 0 && !currentContent.value && data.context?.topic) {
            const ctx = data.context;
            const typeLabel = contentTypeLabels[ctx.content_type] || ctx.content_type || 'content';
            firstDraftPrompt = `Create a ${typeLabel} post about: ${ctx.topic}`;
            if (ctx.audience) {
                firstDraftPrompt += `\n\nThe target audience is ${ctx.audience}.`;
            }
            if (ctx.language) {
                firstDraftPrompt += `\n\nWrite all content in ${ctx.language}.`;
            }
        }

        // The conversation only stores a prompt once Spark has answered it, so
        // after a reload mid-generation, follow that run instead of starting another
        if (await isGenerationRunning()) {
            if (firstDraftPrompt) {
                chatMessages.value.push({ role: 'user', content: firstDraftPrompt });
            }
            isGenerating.value = true;
            startPolling();
            return;
        }

        if (firstDraftPrompt) {
            await sendMessage(firstDraftPrompt);
        }
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const isGenerationRunning = async () => {
    if (!props.pollUrl) return false;

    try {
        const res = await fetch(props.pollUrl);
        const data = await res.json();
        return data.status === 'pending' || data.status === 'processing';
    } catch (e) {
        return false;
    }
};

const handleOnboardingComplete = async (context) => {
    if (!props.pieceId) {
        // Create the piece first via form POST
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = props.storeUrl;

        const csrf = document.querySelector('meta[name="csrf-token"]');
        if (csrf) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = '_token';
            input.value = csrf.content;
            form.appendChild(input);
        }

        for (const [key, value] of Object.entries(context)) {
            if (value) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
            }
        }

        document.body.appendChild(form);
        form.submit();
        return;
    }

    pageContext.value = context;

    // Auto-generate first content
    const topic = context.topic || '';
    if (topic) {
        const typeLabel = contentTypeLabels[context.content_type] || context.content_type;
        let prompt = `Create a ${typeLabel} post about: ${topic}`;
        if (context.audience) {
            prompt += `\n\nThe target audience is ${context.audience}.`;
        }
        if (context.language) {
            prompt += `\n\nWrite all content in ${context.language}.`;
        }
        await sendMessage(prompt);
    }
};

const sendMessage = async (text) => {
    if (!text || isGenerating.value || !props.generateUrl) return;

    // Send what the editor shows, unsaved manual edits included, so Spark
    // revises this version rather than its own last draft
    if (isEditing.value) {
        currentContent.value = editableContent.value;
        isEditing.value = false;
    }

    chatMessages.value.push({ role: 'user', content: text });
    isGenerating.value = true;
    generateError.value = null;
    generatingStep.value = '';

    try {
        const res = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify({ prompt: text, content: currentContent.value }),
        });

        if (res.ok) {
            const data = await res.json();

            if (data.status === 'budget_exceeded') {
                isGenerating.value = false;
                pushBudgetExceededNotice();
                return;
            }

            startPolling();
        } else {
            isGenerating.value = false;
            generateError.value = 'Failed to start generation.';
        }
    } catch (e) {
        isGenerating.value = false;
        generateError.value = 'Network error. Please try again.';
    }
};

// Spark's reply when there is no AI usage left. It only lives here: the
// server never stores it, so it is not part of the conversation Spark is given.
const pushBudgetExceededNotice = () => {
    chatMessages.value.push({
        role: 'assistant',
        notice: _mft('content-creator:editor.budgetExceeded'),
        budgetExceeded: true,
    });
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    let attempts = 0;
    let pollDelay = 1000; // Start fast (1s)
    const schedulePoll = () => {
        pollTimer.value = setTimeout(async () => {
            attempts++;
            if (attempts > 100) {
                isGenerating.value = false;
                generateError.value = 'Generation timed out. Please try again.';
                return;
            }

            // Adaptive: 1s for first 10, then 3s
            if (attempts > 10) pollDelay = 3000;

            try {
                const res = await fetch(props.pollUrl);
                const data = await res.json();

                if (data.status === 'processing' && data.step) {
                    generatingStep.value = data.step;
                }

                if (data.status === 'completed') {
                    currentContent.value = data.content || '';
                    imageUrl.value = data.image_url || null;
                    chatMessages.value.push({ role: 'assistant', content: data.content });
                    isGenerating.value = false;
                    generatingStep.value = '';

                    // Capture LLM-suggested schedule
                    if (data.suggested_schedule && !scheduleActivated.value) {
                        suggestedSchedule.value = data.suggested_schedule;
                    }

                    // If image is still generating, start image polling
                    if (data.image_generating && !data.image_url) {
                        isImageGenerating.value = true;
                        startImagePolling();
                    }
                    return; // Stop content polling
                } else if (data.status === 'budget_exceeded') {
                    isGenerating.value = false;
                    generatingStep.value = '';
                    pushBudgetExceededNotice();
                    return;
                } else if (data.status === 'failed') {
                    isGenerating.value = false;
                    generateError.value = _mft('error:error.generalTryAgain');
                    console.warn(data.error || 'Generation failed without error message');
                    return;
                }
            } catch (e) {
                // continue polling
            }
            schedulePoll();
        }, pollDelay);
    };
    schedulePoll();
};

const startImagePolling = () => {
    if (imagePollTimer.value) clearTimeout(imagePollTimer.value);

    let imageAttempts = 0;
    const scheduleImagePoll = () => {
        imagePollTimer.value = setTimeout(async () => {
            imageAttempts++;
            if (imageAttempts > 40) { // ~2 min max
                isImageGenerating.value = false;
                return;
            }

            try {
                const res = await fetch(props.pollUrl);
                const data = await res.json();

                if (data.image_url) {
                    imageUrl.value = data.image_url;
                    isImageGenerating.value = false;
                    return;
                }
                if (!data.image_generating) {
                    isImageGenerating.value = false;
                    return;
                }
            } catch (e) {
                // continue polling
            }
            scheduleImagePoll();
        }, 3000);
    };
    scheduleImagePoll();
};

const handleSave = async () => {
    if (!props.saveUrl || isSaving.value) return;
    isSaving.value = true;
    savingState.value = 'waiting';

    try {
        const res = await fetch(props.saveUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify({
                content: isEditing.value ? editableContent.value : currentContent.value,
            }),
        });

        // Rejected (e.g. over the length limit): stay in edit mode so the edits are kept
        if (!res.ok) {
            savingState.value = 'error';
            return;
        }

        if (isEditing.value) {
            currentContent.value = editableContent.value;
            isEditing.value = false;
        }
        savingState.value = 'success';
        setTimeout(() => { savingState.value = ''; }, 2000);
    } catch (e) {
        savingState.value = 'error';
    } finally {
        isSaving.value = false;
    }
};

const toggleEdit = () => {
    if (!isEditing.value) {
        editableContent.value = currentContent.value;
    }
    isEditing.value = !isEditing.value;
};

const copyToClipboard = async () => {
    try {
        copyingState.value = 'waiting';
        await navigator.clipboard.writeText(currentContent.value);
        setTimeout(() => {
            copyingState.value = 'success';
        }, 50);
    } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = currentContent.value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
    }
};

const activateSchedule = async () => {
    if (!suggestedSchedule.value || !props.createScheduleUrl) return;
    activatingSchedule.value = true;
    try {
        const response = await fetch(props.createScheduleUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify({
                name: suggestedSchedule.value.name,
                schedule_days: suggestedSchedule.value.schedule_days,
                schedule_time: suggestedSchedule.value.schedule_time,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
        });
        const data = await response.json();
        if (data.schedule_id) {
            scheduleActivated.value = true;
        }
    } catch (e) {
        console.error('Failed to create schedule', e);
    } finally {
        activatingSchedule.value = false;
    }
};

const dismissError = () => {
    generateError.value = null;
};

const wordCount = computed(() => {
    const text = currentContent.value.trim();
    if (!text) return 0;
    return text.split(/\s+/).length;
});

const charCount = computed(() => currentContent.value.length);
</script>

<template>
    <div class="flex flex-col h-dvh">
        <!-- Toolbar -->
        <div class="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 shadow-sm z-10">
            <div class="flex items-center space-x-3">
                <a :href="exitUrl">
                    <button-base style-type="secondary">
                        <template #icon>
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
                            </svg>
                        </template>
                        <mf-translate domainkey="content-creator:editor.back"></mf-translate>
                    </button-base>
                </a>
                <span
                    v-if="contentType && pieceId"
                    class="px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="contentTypeColors[contentType] || 'bg-gray-100 text-gray-700'"
                >
                    {{ contentTypeLabels[contentType] || contentType }}
                </span>
                <span v-if="currentContent" class="text-xs text-gray-400">
                    {{ wordCount }} {{ _mft('content-creator:editor.words') }}
                    &middot;
                    {{ charCount }} {{ _mft('content-creator:editor.chars') }}
                </span>
            </div>

            <div class="flex items-center space-x-2">
                <!-- Historical posts -->
                <button-base
                    @click="showHistoricalPosts = !showHistoricalPosts"
                    style-type="default"
                    size="xs"
                    class="!border-0 !bg-transparent !px-2 !py-2"
                    :title="_mft('content-creator:historicalPosts.title')"
                >
                    <template #icon>
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                        </svg>
                    </template>
                </button-base>

                <!-- Copy -->
                <button-base
                    v-if="currentContent"
                    @click="copyToClipboard"
                    style-type="default"
                    size="xs"
                    class="!border-0 !bg-transparent !px-2 !py-2"
                    title="Copy"
                    :state="copyingState"
                >
                    <template #icon>
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                        </svg>
                    </template>
                </button-base>

                <!-- Edit toggle -->
                <button-base
                    v-if="currentContent"
                    @click="toggleEdit"
                    style-type="default"
                    :class="{ '!bg-gray-200': isEditing }"
                    size="xs"
                    class="!border-0 !bg-transparent !px-2 !py-2"
                    :title="isEditing ? _mft('content-creator:editor.preview') : _mft('content-creator:editor.edit')"
                >
                    <template #icon>
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        </svg>
                    </template>
                </button-base>

                <!-- Save -->
                <button-base
                    v-if="currentContent"
                    style-type="success"
                    @click="handleSave"
                    :state="savingState"
                    class="ml-2"
                >
                    <mf-translate domainkey="content-creator:editor.save"></mf-translate>
                </button-base>
            </div>
        </div>

        <!-- Error banner -->
        <div v-if="generateError" class="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span class="text-sm text-red-700">{{ generateError }}</span>
            <button-base @click="dismissError" style-type="default" size="xs" class="ml-4 !border-0 !bg-transparent !text-red-500">
                <template #icon>
                    <span aria-hidden="true">&times;</span>
                </template>
            </button-base>
        </div>

        <!-- Main content area -->
        <div class="flex flex-1 overflow-hidden">
            <!-- Content preview pane (LEFT) -->
            <div class="flex-1 bg-gray-100 flex items-start justify-center p-4 overflow-auto">
                <ai-content-creator-content-preview
                    :content="currentContent"
                    :content-type="contentType"
                    :is-editing="isEditing"
                    :editable-content="editableContent"
                    :content-type-labels="contentTypeLabels"
                    :content-type-colors="contentTypeColors"
                    :image-url="imageUrl"
                    :image-generating="isImageGenerating"
                    :is-generating="isGenerating"
                    :generating-step="generatingStep"
                    @update:editable-content="editableContent = $event"
                />
            </div>

            <!-- Right sidebar: Onboarding or Chat -->
            <div class="w-[400px] flex-shrink-0 border-l border-gray-200 bg-white flex flex-col min-h-0">
                <!-- Onboarding (no piece yet) -->
                <ai-content-creator-onboarding
                    v-if="showOnboarding"
                    class="flex-1 min-h-0"
                    :flowie-handoff="flowieHandoff"
                    @complete="handleOnboardingComplete"
                />

                <!-- Chat -->
                <ai-content-creator-chat
                    v-else
                    class="flex-1 min-h-0"
                    :messages="chatMessages"
                    :is-generating="isGenerating"
                    :suggested-schedule="suggestedSchedule"
                    :schedule-activated="scheduleActivated"
                    :activating-schedule="activatingSchedule"
                    @send="sendMessage"
                    @activate-schedule="activateSchedule"
                />
            </div>
        </div>

        <!-- Historical posts modal -->
        <ai-content-creator-historical-posts
            v-if="showHistoricalPosts"
            :posts-url="historicalPostsUrl"
            :store-url="historicalPostsStoreUrl"
            @close="showHistoricalPosts = false"
        />
    </div>
</template>
